import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule, AbstractControl, ValidatorFn } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { EditorModule } from '@tinymce/tinymce-angular';
import { Storage } from '@angular/fire/storage';
import { PostService, Post, PostCategory, PostCreate, PostUpdate } from '../../services/post.service';
import { WebsiteService, SupportedLanguage, LanguageOption, Domain } from '../../services/website.service';
import { NotificationService } from '../../services/notification.service';
import { ImageUploadService } from '../../services/image-upload.service';
import { NotificationComponent } from '../../components/notification/notification.component';
import { MediaSelectorModalComponent } from '../pages/page-editor/components/media-selector-modal/media-selector-modal.component';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-edit-post',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, EditorModule, NotificationComponent, MediaSelectorModalComponent],
  templateUrl: './edit-post.component.html'
})
export class EditPostComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  postForm!: FormGroup;
  postId: string | null = null;
  isNew = false;
  /** Wordt alleen true tijdens taalwisseling (bestaand doc laden) */
  isLoading = false;
  isSaving = false;
  /** True zolang post-data nog geladen wordt (form is al zichtbaar) */
  isLoadingPost = false;

  /** Het huidige taaldocument dat bewerkt wordt */
  currentPost: Post | null = null;
  /** basePostId die alle taalversies koppelt */
  basePostId: string | null = null;
  /** Welke taalversies bestaan er? (id per taal) */
  languageVersions: Map<string, string> = new Map();

  currentLocale: SupportedLanguage = 'nl';
  availableLanguages: LanguageOption[] = [];
  domains: Domain[] = [];
  categories: (PostCategory & { id: string })[] = [];
  showNewCategoryInput = false;
  newCategoryName = '';
  showMediaSelector = false;
  mediaSelectorTarget: 'featured' | 'og' = 'featured';
  languageNames: Record<string, string> = {
    en: 'English', nl: 'Nederlands', fr: 'Français', de: 'Deutsch', es: 'Español', it: 'Italiano'
  };

  editorConfig = {
    base_url: '/tinymce',
    suffix: '.min',
    promotion: false,
    skin: 'oxide-dark',
    content_css: 'dark',
    plugins: [
      'advlist', 'autolink', 'lists', 'link', 'image', 'charmap',
      'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
      'insertdatetime', 'media', 'table', 'help', 'wordcount', 'quickbars'
    ],
    toolbar: 'undo redo | blocks | bold italic forecolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | image media | styleselect | help',
    image_advtab: true,
    image_dimensions: true,
    image_caption: true,
    quickbars_selection_toolbar: 'bold italic | quicklink h2 h3 blockquote',
    quickbars_insert_toolbar: 'image media quicktable',
    content_style: 'body { background-color: #1F2937; color: #D1D5DB; font-size: 14px; line-height: 1.5; } img { max-width: 100%; height: auto; }',
    images_upload_handler: (blobInfo: any) => this.uploadEditorImage(blobInfo),
    file_picker_callback: (cb: any, _value: any, meta: any) => {
      const input = document.createElement('input');
      input.setAttribute('type', 'file');
      input.setAttribute('accept', meta.filetype === 'image' ? 'image/*' : 'video/*,audio/*');
      input.onchange = () => {
        const file = input.files?.[0];
        if (!file) return;
        this.uploadFile(file).then(url => cb(url, { title: file.name }));
      };
      input.click();
    },
    automatic_uploads: true,
    paste_data_images: true,
    images_file_types: 'jpeg,jpg,png,gif,webp'
  };

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private storage: Storage,
    private postService: PostService,
    private websiteService: WebsiteService,
    private notificationService: NotificationService,
    private imageUploadService: ImageUploadService
  ) {}

  async ngOnInit() {
    this.postId = this.route.snapshot.paramMap.get('id');
    this.isNew = !this.postId || this.postId === 'new';

    // Formulier DIRECT initialiseren → UI verschijnt meteen
    this.initForm();

    // Data laden op de achtergrond (UI is al zichtbaar)
    if (!this.isNew) this.isLoadingPost = true;

    const currentDomain = this.websiteService.getCurrentDomain();
    const postPromise = (!this.isNew && this.postId)
      ? this.postService.getPostById(this.postId)
      : Promise.resolve(null);

    // Alles parallel ophalen
    const [domainsResult, postResult] = await Promise.all([
      this.websiteService.getDomains(),
      postPromise
    ]);

    this.domains = domainsResult;
    if (this.domains.length === 0) {
      this.notificationService.show({ message: 'Geen domeinen beschikbaar', type: 'error' });
      this.isLoadingPost = false;
      return;
    }

    const domain = currentDomain && this.domains.some(d => d.name === currentDomain)
      ? currentDomain
      : this.domains[0].name;

    // Settings + categories parallel
    const [settings, categoriesRaw] = await Promise.all([
      this.websiteService.getWebsiteSettings(domain),
      this.websiteService.getCategories(domain)
    ]);

    if (settings?.availableLanguages?.length) {
      this.availableLanguages = settings.availableLanguages;
      const storedLang = this.websiteService.getCurrentLanguage();
      const availableCodes = this.availableLanguages.map(l => l.code);
      if (availableCodes.includes(storedLang)) {
        this.currentLocale = storedLang;
      } else {
        this.currentLocale = (settings.defaultLanguage || settings.availableLanguages[0].code) as SupportedLanguage;
      }
    } else {
      this.availableLanguages = [{ code: 'nl', name: 'Nederlands' }, { code: 'en', name: 'English' }];
    }

    this.categories = categoriesRaw.map(c => ({ ...c, domain }));

    if (postResult) {
      this.currentPost = postResult;
      this.currentLocale = postResult.language || 'nl';
      this.basePostId = postResult.basePostId || postResult.id;
      this.patchForm(postResult);

      // Taalversies laden — niet-blokkerend
      this.loadLanguageVersions();

      const firstDom = postResult.domains?.length ? postResult.domains[0] : postResult.domain;
      if (firstDom && firstDom !== domain) {
        this.websiteService.getCategories(firstDom).then(cats => {
          this.categories = cats.map(c => ({ ...c, domain: firstDom! }));
        });
      }
    } else if (!this.isNew) {
      this.notificationService.show({ message: 'Bericht niet gevonden', type: 'error' });
      this.router.navigate(['/posts']);
    } else {
      this.postForm.patchValue({ domains: [domain] });
    }

    this.isLoadingPost = false;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private domainsValidator(): ValidatorFn {
    return (c: AbstractControl) =>
      Array.isArray(c.value) && c.value.length > 0 ? null : { required: true };
  }

  /**
   * Formulier met simpele string-velden (geen nested FormGroups meer per taal).
   */
  private initForm() {
    this.postForm = this.fb.group({
      domains: [[] as string[], [Validators.required, this.domainsValidator()]],
      title: [''],
      content: [''],
      status: ['draft'],
      categoryIds: [[] as string[]],
      featuredImage: [''],
      slug: ['', Validators.required],
      metaTitle: [''],
      metaDescription: [''],
      metaKeywords: [[] as string[]],
      ogTitle: [''],
      ogDescription: [''],
      ogImage: [''],
      htmlBlock: ['']
    });

    this.postForm.get('domains')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (domainNames: string[]) => {
        const first = domainNames?.length ? domainNames[0] : null;
        if (first) {
          this.categories = (await this.websiteService.getCategories(first)).map(c => ({ ...c, domain: first }));
        }
      });
  }

  /**
   * Vul het formulier met data uit een Post document (per taal).
   */
  private patchForm(post: Post) {
    this.postForm.patchValue({
      title: post.title || '',
      content: post.content || '',
      domains: post.domains?.length ? [...post.domains] : (post.domain ? [post.domain] : []),
      status: post.status || 'draft',
      categoryIds: post.categoryIds ?? [],
      featuredImage: post.featuredImage ?? '',
      slug: post.slug ?? '',
      metaTitle: post.metaTitle ?? '',
      metaDescription: post.metaDescription ?? '',
      metaKeywords: post.metaKeywords ?? [],
      ogTitle: post.ogTitle ?? '',
      ogDescription: post.ogDescription ?? '',
      ogImage: post.ogImage ?? '',
      htmlBlock: post.htmlBlock ?? ''
    });
  }

  /**
   * Laad alle taalversies van de huidige post om te weten welke talen bestaan.
   */
  private async loadLanguageVersions() {
    if (!this.basePostId) return;

    this.languageVersions.clear();
    const versions = await this.postService.getPostsByBaseId(this.basePostId);
    for (const v of versions) {
      this.languageVersions.set(v.language, v.id);
    }
  }

  /**
   * Controleer of er een taalversie bestaat voor een bepaalde taal.
   */
  hasLanguageVersion(langCode: string): boolean {
    return this.languageVersions.has(langCode);
  }

  /**
   * Wissel van taal – laadt een ander Firestore document of maakt een nieuw formulier.
   */
  async setCurrentLocale(code: string) {
    if (code === this.currentLocale) return;

    // Sla huidige wijzigingen eerst op (als er al een document is)
    if (this.currentPost && !this.isNew) {
      await this.saveCurrentSilently();
    }

    const previousLocale = this.currentLocale;
    this.currentLocale = code as SupportedLanguage;

    // Bestaand document voor deze taal?
    const existingDocId = this.languageVersions.get(code);
    if (existingDocId) {
      // Laad het bestaande document
      this.isLoading = true;
      const post = await this.postService.getPostById(existingDocId);
      if (post) {
        this.currentPost = post;
        this.postId = post.id;
        this.isNew = false;
        this.patchForm(post);
      }
      this.isLoading = false;
    } else {
      // Geen document voor deze taal: maak een leeg formulier
      // Bewaar domains, categoryIds, status en slug van het huidige document
      const currentDomains = this.postForm.get('domains')?.value || [];
      const currentCategoryIds = this.postForm.get('categoryIds')?.value || [];
      const currentStatus = this.postForm.get('status')?.value || 'draft';
      const currentSlug = this.postForm.get('slug')?.value || '';
      const currentFeaturedImage = this.postForm.get('featuredImage')?.value || '';

      this.currentPost = null;
      this.postId = null;
      this.isNew = true;

      this.postForm.patchValue({
        title: '',
        content: '',
        domains: currentDomains,
        status: currentStatus,
        categoryIds: currentCategoryIds,
        featuredImage: currentFeaturedImage,
        slug: currentSlug,
        metaTitle: '',
        metaDescription: '',
        metaKeywords: [],
        ogTitle: '',
        ogDescription: '',
        ogImage: '',
        htmlBlock: ''
      });
    }
  }

  /**
   * Sla het huidige document stil op (bij taalwisseling).
   */
  private async saveCurrentSilently() {
    if (!this.currentPost?.id) return;

    try {
      const payload = this.buildPayload();
      await this.postService.updatePost(this.currentPost.id, payload as PostUpdate);
    } catch (err) {
      console.error('Auto-save bij taalwisseling mislukt:', err);
    }
  }

  private async uploadEditorImage(blobInfo: any): Promise<string> {
    const file = blobInfo.blob() as File;
    const variants = await this.imageUploadService.uploadImageWithVariants(file);
    this.notificationService.show({ message: 'Afbeelding geüpload', type: 'success' });
    return variants.webp1400;
  }

  private async uploadFile(file: File): Promise<string> {
    const variants = await this.imageUploadService.uploadImageWithVariants(file);
    return variants.webp1400;
  }

  /**
   * Vercel preview URL for this post, scoped to the current domain.
   * ?domain= triggers cache bypass on the frontend.
   */
  get previewUrl(): string | null {
    const slug = this.postForm?.get('slug')?.value;
    if (!slug) return null;
    const domain = this.websiteService.getCurrentDomain();
    if (!domain) return null;
    const prefix = this.postUrlPrefix;
    return `${environment.previewBaseUrl}/${encodeURIComponent(prefix)}/${encodeURIComponent(slug)}?domain=${encodeURIComponent(domain)}`;
  }

  get postUrlPrefix(): string {
    const categoryIds = this.postForm?.get('categoryIds')?.value || [];
    if (categoryIds.length > 0) {
      const firstCat = this.categories.find(c => c.id === categoryIds[0]);
      if (firstCat) {
        return firstCat.slug || firstCat.name.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-');
      }
    }
    return 'posts';
  }

  private generateSlug(t: string): string {
    return t.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').trim();
  }

  async onTitleChange(val: string) {
    if (!this.postForm.get('slug')?.value) {
      const slug = this.generateSlug(val) || 'untitled';
      this.postForm.patchValue({ slug });
    }
  }

  getLanguageName(code: string): string {
    return this.languageNames[code] || this.availableLanguages.find(l => l.code === code)?.name || code;
  }

  openMediaSelector(target: 'featured' | 'og') {
    this.mediaSelectorTarget = target;
    this.showMediaSelector = true;
  }

  closeMediaSelector() {
    this.showMediaSelector = false;
  }

  onMediaSelected(url: string) {
    if (this.mediaSelectorTarget === 'featured') {
      this.postForm.patchValue({ featuredImage: url });
    } else {
      this.postForm.patchValue({ ogImage: url });
    }
    this.closeMediaSelector();
  }

  async uploadFeaturedImage(e: Event) {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file) return;
    try {
      const url = await this.uploadFile(file);
      this.postForm.patchValue({ featuredImage: url });
      this.notificationService.show({ message: 'Afbeelding geüpload', type: 'success' });
    } catch {
      this.notificationService.show({ message: 'Upload mislukt', type: 'error' });
    }
  }

  async uploadOgImage(e: Event) {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file) return;
    try {
      const url = await this.uploadFile(file);
      this.postForm.patchValue({ ogImage: url });
      this.notificationService.show({ message: 'OG afbeelding geüpload', type: 'success' });
    } catch {
      this.notificationService.show({ message: 'Upload mislukt', type: 'error' });
    }
  }

  onKeywordInput(ev: KeyboardEvent) {
    const el = ev.target as HTMLInputElement;
    const v = el.value.trim();
    if ((ev.key === 'Enter' || ev.key === ',') && v) {
      ev.preventDefault();
      const arr = (this.postForm.get('metaKeywords')?.value || []) as string[];
      const word = v.replace(/,/g, '');
      if (word && !arr.includes(word)) {
        this.postForm.patchValue({ metaKeywords: [...arr, word] });
      }
      el.value = '';
    }
  }

  removeKeyword(k: string) {
    const arr = ((this.postForm.get('metaKeywords')?.value || []) as string[]).filter((x: string) => x !== k);
    this.postForm.patchValue({ metaKeywords: arr });
  }

  isCategorySelected(id: string): boolean {
    return (this.postForm.get('categoryIds')?.value || []).includes(id);
  }

  toggleCategory(id: string): void {
    const arr = (this.postForm.get('categoryIds')?.value || []).slice() as string[];
    const i = arr.indexOf(id);
    if (i >= 0) arr.splice(i, 1);
    else arr.push(id);
    this.postForm.patchValue({ categoryIds: arr });
  }

  cancelNewCategory() {
    this.showNewCategoryInput = false;
    this.newCategoryName = '';
  }

  isDomainSelected(domainName: string): boolean {
    return (this.postForm.get('domains')?.value || []).includes(domainName);
  }

  toggleDomain(domainName: string): void {
    const arr = (this.postForm.get('domains')?.value || []).slice() as string[];
    const i = arr.indexOf(domainName);
    if (i >= 0) arr.splice(i, 1);
    else arr.push(domainName);
    this.postForm.patchValue({ domains: arr });
    this.postForm.get('domains')?.updateValueAndValidity();
  }

  async addCategory() {
    const domains = (this.postForm.get('domains')?.value || []) as string[];
    const domain = domains[0] || this.websiteService.getCurrentDomain();
    if (!domain) {
      this.notificationService.show({ message: 'Selecteer minimaal één domein', type: 'error' });
      return;
    }
    const name = this.newCategoryName.trim();
    if (!name) {
      this.notificationService.show({ message: 'Voer een categorienaam in', type: 'error' });
      return;
    }
    const bestaand = this.categories.find((c) => c.name.toLowerCase() === name.toLowerCase());
    if (bestaand) {
      const arr = (this.postForm.get('categoryIds')?.value || []).slice() as string[];
      if (!arr.includes(bestaand.id)) arr.push(bestaand.id);
      this.postForm.patchValue({ categoryIds: arr });
      this.cancelNewCategory();
      this.notificationService.show({ message: 'Categorie bestaat al, geselecteerd', type: 'success' });
      return;
    }
    try {
      const id = await this.websiteService.createCategory(domain, name);
      this.categories = (await this.websiteService.getCategories(domain)).map((c) => ({ ...c, domain }));
      this.toggleCategory(id);
      this.cancelNewCategory();
      this.notificationService.show({ message: 'Categorie toegevoegd', type: 'success' });
    } catch (e) {
      console.error('Categorie toevoegen mislukt:', e);
      this.notificationService.show({
        message: e instanceof Error ? e.message : 'Categorie toevoegen mislukt',
        type: 'error'
      });
    }
  }

  cancel() {
    this.router.navigate(['/posts']);
  }

  /**
   * Bouw de payload op vanuit het formulier.
   */
  private buildPayload(): Record<string, unknown> {
    const v = this.postForm.value;
    const domains = (this.postForm.get('domains')?.value || []) as string[];

    const payload: Record<string, unknown> = {
      title: v.title || '',
      content: v.content || '',
      language: this.currentLocale,
      basePostId: this.basePostId || null,
      status: v.status || 'draft',
      categoryIds: (this.postForm.get('categoryIds')?.value || []) as string[],
      domains,
      slug: String(this.postForm.get('slug')?.value ?? v.slug ?? '').trim(),
      metaTitle: v.metaTitle || '',
      metaDescription: v.metaDescription || '',
      metaKeywords: v.metaKeywords || [],
      ogTitle: v.ogTitle || '',
      ogDescription: v.ogDescription || ''
    };

    const feat = v['featuredImage'];
    const og = v['ogImage'];
    if (feat && String(feat).trim()) payload['featuredImage'] = String(feat).trim();
    if (og && String(og).trim()) payload['ogImage'] = String(og).trim();

    // htmlBlock: altijd meesturen (ook lege string), anders kan een bestaande
    // waarde niet gewist worden bij updatePost.
    payload['htmlBlock'] = v['htmlBlock'] != null ? String(v['htmlBlock']) : '';

    return payload;
  }

  async save() {
    const domains = (this.postForm.get('domains')?.value || []) as string[];
    if (!domains.length) {
      this.notificationService.show({ message: 'Selecteer minimaal één domein', type: 'error' });
      return;
    }

    const slugControl = this.postForm.get('slug');
    const rawSlug = (slugControl?.value ?? '').trim();

    // Slug ontbreekt: afleiden uit titel
    if (!rawSlug) {
      const title = (this.postForm.get('title')?.value || '').trim();
      const slug = this.generateSlug(title) || 'nieuw-bericht-' + Date.now();
      this.postForm.patchValue({ slug });
      slugControl?.updateValueAndValidity();
    }

    if (!this.postForm.valid) {
      this.notificationService.show({
        message: 'Vul de verplichte velden in (bijv. titel, URL-slug en minimaal één domein)',
        type: 'error'
      });
      return;
    }

    this.isSaving = true;
    try {
      const payload = this.buildPayload();

      if (this.isNew || !this.currentPost?.id) {
        // Nieuw document aanmaken
        const newId = await this.postService.createPost(payload as PostCreate);

        // Als dit de allereerste taalversie is, stel basePostId in op het eigen ID
        if (!this.basePostId) {
          this.basePostId = newId;
          await this.postService.updatePost(newId, { basePostId: newId });
        }

        // Laad het net aangemaakte document
        this.currentPost = await this.postService.getPostById(newId);
        this.postId = newId;
        this.isNew = false;

        // Update taalversies
        this.languageVersions.set(this.currentLocale, newId);

        this.notificationService.show({ message: 'Bericht aangemaakt', type: 'success' });
      } else {
        // Bestaand document bijwerken
        await this.postService.updatePost(this.currentPost.id, payload as PostUpdate);
        this.notificationService.show({ message: 'Bericht bijgewerkt', type: 'success' });
      }

      this.router.navigate(['/posts']);
    } catch (err) {
      console.error('Opslaan bericht mislukt:', err);
      const msg = err && typeof err === 'object' && 'message' in err ? String((err as { message: string }).message) : 'Opslaan mislukt';
      this.notificationService.show({ message: msg, type: 'error' });
    } finally {
      this.isSaving = false;
    }
  }
}
